import React, { useState, useEffect } from 'react'
import { Box, Typography, Modal, CircularProgress, Button, IconButton, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { useNavigate } from 'react-router-dom'
import GradientCircularProgressIndicator from '../../../../components/GradientCircularProgressIndicator'

const GeneratingDocumentModal = ({ 
    open, 
    onClose, 
    loading,
    success,
    error,
    errorMessage: propErrorMessage,
    errorButtonText: propErrorButtonText,
    successMessage: propSuccessMessage,
    successSubMessage: propSuccessSubMessage,
    successNote: propSuccessNote,
    successButtonText: propSuccessButtonText,
    loadingMessage: propLoadingMessage,
    loadingSubMessage: propLoadingSubMessage,
    onSuccess,
    onError,
    showConfirmation = false,
    onConfirm,
    confirmButtonText = 'Continue',
    cancelButtonText = 'Cancel'
}) => {
    const theme = useTheme()
    const navigate = useNavigate()

    const [internalLoadingMessage, setInternalLoadingMessage] = useState('')
    const [internalLoadingSubMessage, setInternalLoadingSubMessage] = useState('')

    const [internalErrorMessage, setInternalErrorMessage] = useState('')
    const [internalErrorButtonText, setInternalErrorButtonText] = useState('')

    const [internalSuccessMessage, setInternalSuccessMessage] = useState('')
    const [internalSuccessSubMessage, setInternalSuccessSubMessage] = useState('')
    const [internalSuccessNote, setInternalSuccessNote] = useState('')
    const [internalSuccessButtonText, setInternalSuccessButtonText] = useState('')

    // Use prop messages if provided, otherwise use internal state
    // Error Message
    const errorMessage = propErrorMessage || internalErrorMessage
    const errorButtonText = propErrorButtonText || internalErrorButtonText

    // Success Message
    const successMessage = propSuccessMessage || internalSuccessMessage
    const successSubMessage = propSuccessSubMessage || internalSuccessSubMessage
    const successNote = propSuccessNote || internalSuccessNote
    const successButtonText = propSuccessButtonText || internalSuccessButtonText

    // Loading Message
    const loadingMessage = propLoadingMessage || internalLoadingMessage
    const loadingSubMessage = propLoadingSubMessage || internalLoadingSubMessage
    
    useEffect(() => {
        if (success) {
            setInternalSuccessMessage('Your Document Is Being Generated')
            setInternalSuccessSubMessage('This can take up to 24 hours.')
            setInternalSuccessNote('You will get an email when it is ready.')
            setInternalSuccessButtonText('Back to Workspace')
            
            // Auto-close after 3 seconds on success
            const autoCloseTimeout = setTimeout(() => {
                if (onClose) {
                    onClose()
                }
            }, 3000)
            
            if (onSuccess) {
                onSuccess()
            }
            
            // Cleanup timeout on unmount
            return () => clearTimeout(autoCloseTimeout)
        }
    }, [success, onSuccess, onClose])

    useEffect(() => {
        if (error) {
            setInternalErrorMessage('Document generation failed. Please try again.')
            setInternalErrorButtonText('Try Again')
            if (onError) {
                onError()
            }
        }
    }, [error, onError])

    useEffect(() => {
        if (loading) {
            setInternalLoadingMessage('Beginning document generation...')
            setInternalLoadingSubMessage('This may take a moment.')
        }
    }, [loading])

    useEffect(() => {
        console.log('open', open)
        console.log('loading', loading)
        console.log('success', success)
        console.log('error', error)
    }, [open, loading, success, error])

    const handleCloseModal = () => {
        onClose()
    }

    const handleRetry = () => {
        // Reset error state and close modal to allow retry
        setInternalErrorMessage('')
        onClose()
    }

    const renderContent = () => {
        if (success) {
            return (
                <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: '48px', color: '#66bb6a' }} />
                            <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                {successMessage}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {successSubMessage}
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {successNote}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            backgroundColor:'#1F477D',
                            color: '#FFFFFF',
                            padding: '13px 28px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: '24px',
                            textTransform: 'none',
                            minWidth: '300px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            '&:hover': {
                                backgroundColor: '#1a3a66',
                            },
                            '&:disabled': {
                                backgroundColor: '#E5E7EB',
                                color: '#6B7280',
                            },
                        }}
                    >
                        {successButtonText}
                    </Button>
                </Stack>
            )
        }
        else if (error) {
            return (
                <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                    <ErrorIcon sx={{ fontSize: '48px', color: '#b00020' }} />
                    <Typography variant="h6" sx={{ textAlign: 'center' }}>
                        Error
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        {errorMessage}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleRetry}
                        sx={{ mt: 2 }}
                    >
                        {errorButtonText}
                    </Button>
                </Stack>
            )
        }
        else if (loading) {
            // Show confirmation if this is the initial warning state
            if (showConfirmation && onConfirm) {
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {loadingMessage}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                            {loadingSubMessage}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    borderRadius: 1.5,
                                    borderColor: '#E5E7EB',
                                    color: '#475569',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#CBD5E1',
                                        backgroundColor: '#F8FAFC'
                                    }
                                }}
                            >
                                {cancelButtonText}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onConfirm}
                                sx={{
                                    borderRadius: 1.5,
                                    backgroundColor: '#3B82F6',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {confirmButtonText}
                            </Button>
                        </Box>
                    </Stack>
                )
            } else {
                // Show normal loading state
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <GradientCircularProgressIndicator size={48} />
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {loadingMessage}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                            {loadingSubMessage}
                        </Typography>
                    </Stack>
                )
            }
        }
        return null
    }

    return (
        <Modal
            open={open}
            onClose={error ? handleCloseModal : undefined} // Only allow closing on error
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '384px',
                    minHeight: '240px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    padding: '32px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}
            >
                {/* Close button only shown on error */}
                {error && (
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                
                {/* Main Content Container */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '160px' }}>
                    {renderContent()}
                </Box>
            </Box>
        </Modal>
    )
}

export default GeneratingDocumentModal
